// PhotoCapture.jsx — a reusable "tap to take a photo, then I'll fill in the details" widget.
// Handles camera capture (mobile) AND file upload (desktop).
import { useRef, useState } from "react";
import { Camera, Upload } from 'lucide-react';

/**
 * @param {Object} props
 * @param {(file: File) => Promise<Object>} props.identify - vision function (e.g. identifyAppliance)
 * @param {(result: Object, file: File) => void} props.onConfirm - called with the AI result + file
 * @param {string} props.entity - "appliance" | "food" | "coffee" | "recipe" — determines copy
 */
export function PhotoCapture({ identify, onConfirm, entity, onCancel }) {
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setError(null);
  };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true); setError(null);
    try {
      const res = await identify(file);
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null); setResult(null); setError(null);
  };

  const COPY = {
    appliance: { title: "Photo of an appliance", hint: "Get it square in the frame. Labels help." },
    food:      { title: "Photo of a food / pantry item", hint: "Front of the package, or the produce itself." },
    coffee:    { title: "Photo of a coffee bag", hint: "Show the front label clearly — roaster, name, roast date." },
    coffee_bag:{ title: "Photo of a coffee bag", hint: "Show the front label — roaster, name, roast date." },
    recipe:    { title: "Photo of a recipe", hint: "Cookbook page, magazine clip, screenshot, handwritten card." },
    receipt:   { title: "Photo of a receipt", hint: "Lay it flat. Good lighting. Crop tight if you can." },
  }[entity] || { title: "Take a photo", hint: "" };

  return (
    <div className="space-y-4">
      {!file && (
        <div>
          <h4 className="font-display text-lg text-ink-700 mb-1">{COPY.title}</h4>
          <p className="text-sm text-ink-500 mb-5">{COPY.hint}</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => cameraInput.current?.click()}
              className="card !p-6 flex flex-col items-center gap-2 hover:border-ember-500 transition-colors"
            >
              <Camera size={28} className="text-ember-500" />
              <div className="font-medium text-sm">Camera</div>
              <div className="text-[11px] text-ink-400">Take a photo now</div>
            </button>
            <button
              onClick={() => fileInput.current?.click()}
              className="card !p-6 flex flex-col items-center gap-2 hover:border-ink-700 transition-colors"
            >
              <Upload size={28} className="text-ink-600" />
              <div className="font-medium text-sm">Upload</div>
              <div className="text-[11px] text-ink-400">From your device</div>
            </button>
          </div>

          {/* Hidden inputs */}
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={e => pickFile(e.target.files?.[0])}
            className="hidden"
          />
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            onChange={e => pickFile(e.target.files?.[0])}
            className="hidden"
          />
        </div>
      )}

      {file && (
        <>
          <div className="relative aspect-square rounded-xl overflow-hidden bg-parchment-100">
            <img src={previewUrl} alt="" className="w-full h-full object-cover" />
            <button
              onClick={reset}
              className="absolute top-2 right-2 bg-ink-700/80 backdrop-blur text-parchment-50 rounded-full p-1.5"
            >
              <X size={14} />
            </button>
            {analyzing && (
              <div className="absolute inset-0 bg-ink-700/60 backdrop-blur-sm flex flex-col items-center justify-center text-parchment-50">
                <Loader2 size={28} className="animate-spin mb-2" />
                <div className="font-mono text-xs uppercase tracking-[0.18em]">analyzing</div>
              </div>
            )}
          </div>

          {error && (
            <div className="card !p-3 bg-ember-500/5 border-ember-500/20">
              <p className="text-sm text-ember-700">{error}</p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {!result && !analyzing && (
              <motion.div
                key="actions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex gap-2"
              >
                <button onClick={reset} className="btn btn-ghost flex-1 justify-center">
                  <RefreshCw size={14} /> Retake
                </button>
                <button onClick={analyze} className="btn btn-primary flex-1 justify-center">
                  <Sparkles size={14} /> Identify with AI
                </button>
              </motion.div>
            )}

            {result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <ResultPreview result={result} entity={entity} />
                <div className="flex gap-2">
                  <button onClick={analyze} className="btn btn-ghost flex-1 justify-center">
                    <Sparkles size={14} /> Try again
                  </button>
                  <button
                    onClick={() => onConfirm(result, file)}
                    className="btn btn-primary flex-1 justify-center"
                  >
                    Looks right
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

function ResultPreview({ result, entity }) {
  return (
    <div className="card !p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">AI says</div>
        {result.confidence && (
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${
            result.confidence === "high" ? "bg-sage-300/20 text-sage-700" :
            result.confidence === "medium" ? "bg-parchment-200 text-ink-500" :
            "bg-ember-500/10 text-ember-700"
          }`}>
            {result.confidence} confidence
          </span>
        )}
      </div>

      {entity === "appliance" && (
        <>
          <div className="font-display text-xl text-ink-700">{result.name}</div>
          <div className="text-sm text-ink-500 mb-2">
            {[result.brand, result.model, result.category].filter(Boolean).join(" · ")}
          </div>
          {result.details?.notable_features?.length > 0 && (
            <div className="text-xs text-ink-400 italic font-display">
              {result.details.notable_features.join(" · ")}
            </div>
          )}
        </>
      )}

      {entity === "food" && (
        <>
          <div className="font-display text-xl text-ink-700">{result.name}</div>
          <div className="text-sm text-ink-500">
            {[result.brand, result.category].filter(Boolean).join(" · ")}
          </div>
          {result.quantity_visible && <div className="text-xs text-ink-400 mt-1">Quantity: {result.quantity_visible}</div>}
          {result.estimated_shelf_life_days && (
            <div className="text-xs text-ink-400">~{result.estimated_shelf_life_days}d shelf life · {result.storage_recommendation}</div>
          )}
        </>
      )}

      {entity === "coffee" && (
        <>
          <div className="font-display text-xl text-ink-700">{result.name}</div>
          <div className="text-sm text-ink-500 mb-2">{result.roaster}</div>
          <div className="flex flex-wrap gap-1.5 text-[11px] text-ink-500">
            {result.roast_level && <span className="tag">{result.roast_level}</span>}
            {result.process && <span className="tag">{result.process}</span>}
            {result.roast_date && <span className="tag">roasted {result.roast_date}</span>}
          </div>
          {result.tasting_notes?.length > 0 && (
            <div className="font-display italic text-sm text-ink-400 mt-2">
              {result.tasting_notes.join(", ")}
            </div>
          )}
        </>
      )}

      {entity === "recipe" && (
        <>
          <div className="font-display text-xl text-ink-700 mb-1">{result.title}</div>
          {result.summary && (
            <div className="font-display italic text-sm text-ink-500 mb-2">{result.summary}</div>
          )}
          <div className="grid grid-cols-3 gap-2 text-[11px] text-ink-500">
            <div>{result.ingredients?.length || 0} ingredients</div>
            <div>{result.steps?.length || 0} steps</div>
            <div>{result.time?.total_min || "?"} min total</div>
          </div>
        </>
      )}
    </div>
  );
}
